// PWA detection and utilities

/**
 * Detect if the app is running as an installed PWA (standalone mode).
 * This works on both iOS (Safari) and Android (Chrome/Firefox).
 */
export function isPwaMode(): boolean {
  // Check display-mode media query (works on most browsers)
  if (window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }

  // iOS Safari standalone mode
  if ((navigator as unknown as { standalone?: boolean }).standalone === true) {
    return true;
  }

  // Android TWA (Trusted Web Activity)
  if (document.referrer.includes("android-app://")) {
    return true;
  }

  return false;
}

/**
 * Open a URL in the system browser, even when running as a PWA.
 * Falls back to regular navigation if system browser opening fails.
 * 
 * @param url - The URL to open
 * @returns true if opened in system browser, false if fallback was used
 */
export function openInSystemBrowser(url: string): boolean {
  if (isPwaMode()) {
    // In PWA mode, window.open with _blank opens in system browser
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    
    if (opened) {
      return true;
    }
    
    // Fallback: try to create and click a link (some browsers need this)
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return true;
    } catch {
      // If all else fails, use regular navigation
      window.location.href = url;
      return false;
    }
  }
  
  // Not in PWA mode - regular navigation is fine
  window.location.href = url;
  return false;
}
